import { ChevronLeftIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React, { useState, useEffect } from 'react';
import { Route, Switch, useHistory, Redirect } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useRewardful from '@dashboard/hooks/useRewardful';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { stripePublicKey } from '@dashboard/services/env';
import { formatPrice } from '@dashboard/services/formatting';
import { getPlan } from '@dashboard/services/plans';
import hasLiveSubscription from '@dashboard/services/hasLiveSubscription';
import { fetcher } from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';

import { SubscriptionButton } from './BillingSettings/scenes/Plan/PlanOverview';
import { UpgradeForm } from './BillingSettings/scenes/Plan/Upgrade';
import SwitchToYearlyOffer from './BillingSettings/scenes/Plan/SwitchToYearlyOffer';
import {
  planCardFeatureGroups,
  resolveCardItemIncluded,
} from '@dashboard/services/paidPlanFeatureGroups';
import { getSubscribedAt } from '@dashboard/services/features';
import SectionHeading from '@dashboard/components/SectionHeading';
import PageHeader from '@dashboard/components/PageHeader';
import scaleBrackets from '@dashboard/services/scaleBrackets';
import { Badge } from '@/dashboard/components/ui/badge';
import { PriceBreakdown } from './BillingSettings/scenes/Plan/Upgrade';

const FeatureListItem = ({ label, tooltip, included = true }) => {
  // Keep the tooltip icon glued to the last word so it never orphans onto its
  // own line; the rest of the label wraps normally.
  const lastSpace = label.lastIndexOf(' ');
  const head = lastSpace === -1 ? '' : label.slice(0, lastSpace + 1);
  const tail = lastSpace === -1 ? label : label.slice(lastSpace + 1);

  return (
    <li
      className={clsx(
        'flex items-start gap-x-1.5',
        !included && 'text-gray-400 dark:text-muted-foreground',
      )}
    >
      {included ? (
        <CheckCircleIcon className="shrink-0 w-4 h-4 mt-0.5 text-foreground" />
      ) : (
        <MinusCircleIcon className="shrink-0 w-4 h-4 mt-0.5 text-gray-300 dark:text-muted-foreground" />
      )}
      <span>
        {head}
        <span className="whitespace-nowrap">
          {tail}
          {tooltip && <InfoTooltip>{tooltip}</InfoTooltip>}
        </span>
      </span>
    </li>
  );
};

// The grouped feature list under a plan card, driven by planCardFeatureGroups.
const PlanCardFeatures = ({ plan, intro }) => {
  const { seller } = useAuth();
  const card = planCardFeatureGroups[plan];

  if (!card) return null;

  // Re-evaluate rows per seller so grandfathered sellers see kept features as
  // included rather than locked.
  const subscribedAt = getSubscribedAt(seller);

  return (
    <div>
      <h4 className="text-sm font-normal text-gray-600 dark:text-muted-foreground mt-4 pb-1">
        {intro}
      </h4>
      {card.groups.map(group => (
        <div key={group.heading} className="border-t border-input mt-3 pt-3">
          <SectionHeading>{group.heading}</SectionHeading>
          <ul className="flex flex-col gap-y-1.5 text-sm text-gray-600 dark:text-muted-foreground mt-2">
            {group.items.map(item => (
              <FeatureListItem
                key={item.label}
                label={item.label}
                tooltip={item.tooltip}
                included={resolveCardItemIncluded(item, plan, subscribedAt)}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// The "SALES LIMIT" block on a plan card. `children` is the (bold) limit line.
const SalesLimit = ({ children, note }) => (
  <div className="border-t border-input mt-4 pt-3">
    <SectionHeading>Sales limit</SectionHeading>
    <div className="text-sm text-gray-600 dark:text-muted-foreground mt-1.5">{children}</div>
    {note && <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1.5">{note}</p>}
  </div>
);

const PlansPage = () => {
  const { isTrialExpired, isCanceled, isTrialing, plan: sellerPlan, seller } = useAuth();
  const history = useHistory();

  const { billing_period: billingPeriodQuery, ...rest } = useUrlQuery();

  const [billingPeriod, setBillingPeriod] = useState(
    billingPeriodQuery || seller.planBillingPeriod || 'month',
  );
  const [selectedSalesRange, setSelectedSalesRange] = useState(10000);
  const unlimitedPlan = getPlan('unlimited');

  const { upgradeFlow = 'upgrade-page' } = useUrlQuery();

  function handleSetBillingPeriod(period) {
    history.replace(
      `/upgrade?${stringify({
        billing_period: period,
        ...rest,
      })}`,
    );
  }

  useEffect(() => {
    if (billingPeriodQuery && billingPeriodQuery !== billingPeriod) {
      setBillingPeriod(billingPeriodQuery);
      return;
    }

    if (seller.planBillingPeriod && seller.planBillingPeriod !== billingPeriod) {
      setBillingPeriod(seller.planBillingPeriod);
      return;
    }
  }, [billingPeriodQuery, seller.planBillingPeriod]);

  useEffect(() => {
    if (sellerPlan.bracket) {
      setSelectedSalesRange(sellerPlan.bracket);
      return;
    }

    if (sellerPlan.handle === 'scale') {
      setSelectedSalesRange(30000);
      return;
    }
  }, [sellerPlan.bracket, sellerPlan.handle]);

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version, sellerPlan.bracket) || {};
  const isCurrentBillingPeriod = billingPeriod === seller.planBillingPeriod;

  const hasActiveSubscription = seller?.subscriptionStatus === 'active';

  const pageTitle = isTrialExpired
    ? 'Your Scale trial has ended'
    : isCanceled
      ? 'Your subscription has ended'
      : 'Select your plan';

  const pageDescription = isTrialExpired
    ? 'Upgrade to a paid plan to keep advanced features, or start for free.'
    : isCanceled
      ? 'Upgrade today to continue accepting payments.'
      : "Pay a fixed monthly price based on your sales volume, plus Stripe's standard processing fees.";

  return (
    <div className="container p-4">
      <div>
        <div className="mb-3">
          <PageHeader title={pageTitle} description={pageDescription} />
        </div>

        <div className="flex justify-end mb-3">
          <ToggleGroup
            type="single"
            spacing={1}
            value={billingPeriod}
            onValueChange={value => {
              if (value) handleSetBillingPeriod(value);
            }}
            className="shrink-0 rounded-full bg-muted p-1"
            aria-label="Payment frequency"
          >
            <ToggleGroupItem
              value="month"
              className="rounded-full px-3 text-muted-foreground hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
            >
              Monthly
            </ToggleGroupItem>
            <ToggleGroupItem
              value="year"
              className="rounded-full px-3 text-muted-foreground hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
            >
              Yearly - get 2 months free
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-4 gap-y-8 md:m-auto pb-8">
            <div className={clsx('border border-input px-4 py-4 relative rounded-lg bg-card')}>
              <div>
                <h3 className="flex items-center text-xl font-semibold pb-4">Free</h3>

                <div
                  className={clsx('flex flex-col mb-4', {
                    'lg:h-44': billingPeriod === 'year',
                    'lg:h-36': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-2xl">
                    <span className="font-semibold">$0</span>{' '}
                    <span className="text-xl font-light text-gray-400 dark:text-muted-foreground -ml-1">
                      / month
                    </span>
                  </h4>

                  <SalesLimit note="No credit card required">
                    <span className="font-semibold text-gray-900 dark:text-foreground">
                      Up to $3K
                    </span>{' '}
                    in monthly sales
                    <InfoTooltip>
                      Checkout pages, events and forms pause once you pass $3K in sales per month.
                      It resets monthly, or when you upgrade.
                    </InfoTooltip>
                  </SalesLimit>
                </div>

                <div className="mb-4">
                  {currentPlan.handle === 'free' ? (
                    <div className="h-9 text-center rounded border border-transparent text-gray-600 dark:text-muted-foreground flex items-center justify-center">
                      <Badge variant="secondary">Your current plan</Badge>
                    </div>
                  ) : (
                    <SubscriptionButton
                      plan={getPlan('free')}
                      billingPeriod={billingPeriod}
                      setSelectedPlanHandle={() => {}}
                      upgradePath="/upgrade/subscribe"
                      upgradeFlow={upgradeFlow}
                    />
                  )}
                </div>

                <PlanCardFeatures plan="free" intro="Features included:" />
              </div>
            </div>

            <div className={clsx('border border-input px-4 py-4 relative rounded-lg bg-card')}>
              <div>
                <h3 className="flex items-center text-xl font-semibold pb-4">Launch</h3>

                <div
                  className={clsx('flex flex-col mb-4', {
                    'lg:h-44': billingPeriod === 'year',
                    'lg:h-36': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-2xl">
                    <span className="font-semibold">
                      {billingPeriod === 'month' ? '$29' : '$24'}
                    </span>{' '}
                    <span className="text-xl font-light text-gray-400 dark:text-muted-foreground -ml-1">
                      / month
                    </span>
                    <span className="mt-1 text-sm font-light text-gray-400 dark:text-muted-foreground block">
                      {billingPeriod === 'year' ? '($290 billed annually)' : ''}
                    </span>
                  </h4>

                  <SalesLimit>
                    <span className="font-semibold text-gray-900 dark:text-foreground">
                      Up to $3K
                    </span>{' '}
                    in monthly sales
                    <InfoTooltip>
                      We'll never stop you from taking payments. Check out our{' '}
                      <Link href="/docs/articles/pricing-fees" target="blank" variant="light">
                        pricing guide
                      </Link>{' '}
                      for more information
                    </InfoTooltip>
                  </SalesLimit>
                </div>

                <div className="mb-4">
                  {currentPlan.handle === 'launch' &&
                  isCurrentBillingPeriod &&
                  hasActiveSubscription ? (
                    <div className="h-9 text-center rounded border border-transparent text-gray-600 dark:text-muted-foreground flex items-center justify-center">
                      <Badge variant="secondary">Your current plan</Badge>
                    </div>
                  ) : (
                    <SubscriptionButton
                      plan={getPlan('launch')}
                      billingPeriod={billingPeriod}
                      setSelectedPlanHandle={() => {}}
                      upgradePath="/upgrade/subscribe"
                      upgradeFlow={upgradeFlow}
                    />
                  )}
                </div>

                <PlanCardFeatures
                  plan="launch"
                  intro={
                    <>
                      Everything in{' '}
                      <span className="font-semibold text-black dark:text-foreground">Free</span>,
                      plus:
                    </>
                  }
                />
              </div>
            </div>

            <div className={clsx('border border-input px-4 py-4 relative rounded-lg bg-card')}>
              <div>
                <h3 className="flex items-center gap-x-2 text-xl font-semibold pb-4">
                  Scale
                  <Badge variant="success">Recommended</Badge>
                  {isTrialing && currentPlan.handle === 'scale' && (
                    <Badge variant="secondary">Your trial</Badge>
                  )}
                </h3>

                <div
                  className={clsx('flex flex-col mb-4', {
                    'lg:h-44': billingPeriod === 'year',
                    'lg:h-36': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-2xl">
                    <span className="font-semibold">
                      {billingPeriod === 'month'
                        ? `${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .monthlyPrice,
                            currency: 'USD',
                          })}`
                        : `${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .yearlyPrice,
                            currency: 'USD',
                          })}`}
                    </span>{' '}
                    <span className="text-xl font-light text-gray-400 dark:text-muted-foreground -ml-1">
                      / month
                    </span>
                    <span className="mt-1 text-sm font-light text-gray-400 dark:text-muted-foreground block">
                      {billingPeriod === 'year'
                        ? `(${formatPrice({
                            amount: scaleBrackets.find(b => b.value === selectedSalesRange)
                              .yearlyPriceFull,
                            currency: 'USD',
                          })} billed annually)`
                        : ''}
                    </span>
                  </h4>

                  <SalesLimit>
                    <div className="w-full flex items-center gap-x-2">
                      <Select
                        value={String(selectedSalesRange)}
                        onValueChange={value => setSelectedSalesRange(Number(value))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {scaleBrackets.map(bracket => (
                            <SelectItem key={bracket.value} value={String(bracket.value)}>
                              {bracket.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InfoTooltip>
                        We'll never stop you from taking payments. Check out our{' '}
                        <a
                          href="https://checkoutpage.com/docs/articles/pricing-fees"
                          className="underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          pricing guide
                        </a>{' '}
                        for more information
                      </InfoTooltip>
                    </div>
                  </SalesLimit>
                </div>

                <div className="mb-4">
                  {sellerPlan.handle === 'scale' &&
                  sellerPlan.bracket === selectedSalesRange &&
                  isCurrentBillingPeriod &&
                  hasActiveSubscription ? (
                    <div className="h-9 text-center rounded border border-transparent text-gray-600 dark:text-muted-foreground flex items-center justify-center">
                      <Badge variant="secondary">Your current plan</Badge>
                    </div>
                  ) : (
                    <>
                      <SubscriptionButton
                        plan={getPlan('scale')}
                        billingPeriod={billingPeriod}
                        setSelectedPlanHandle={() => {}}
                        upgradePath="/upgrade/subscribe"
                        upgradeFlow={upgradeFlow}
                        selectedScaleBracket={selectedSalesRange}
                      />
                    </>
                  )}
                </div>

                <PlanCardFeatures
                  plan="scale"
                  intro={
                    <>
                      Everything in{' '}
                      <span className="font-semibold text-black dark:text-foreground">Launch</span>,
                      plus:
                    </>
                  }
                />
              </div>
            </div>

            <div className="border border-input px-4 py-4 rounded-lg bg-card">
              <div>
                <h3 className="flex items-center gap-x-2 text-xl font-semibold pb-4">
                  Unlimited
                  {currentPlan.handle === 'unlimited' && hasActiveSubscription && (
                    <Badge variant="secondary">Your current plan</Badge>
                  )}
                </h3>

                <div
                  className={clsx('flex flex-col mb-4', {
                    'lg:h-44': billingPeriod === 'year',
                    'lg:h-36': billingPeriod === 'month',
                  })}
                >
                  <h4 className="text-2xl">
                    <span className="font-semibold">
                      {formatPrice({ amount: unlimitedPlan.yearlyPrice, currency: 'USD' })}
                    </span>{' '}
                    <span className="text-xl font-light text-gray-400 dark:text-muted-foreground -ml-1">
                      / month
                    </span>
                    <span className="mt-1 text-sm font-light text-gray-400 dark:text-muted-foreground block">
                      ({formatPrice({ amount: unlimitedPlan.yearlyPriceFull, currency: 'USD' })}{' '}
                      billed annually)
                    </span>
                  </h4>

                  <SalesLimit note="No sales limit and no overage fees.">
                    <span className="font-semibold text-gray-900 dark:text-foreground">
                      Unlimited
                    </span>
                  </SalesLimit>
                </div>

                <div className="mb-4">
                  {currentPlan.handle === 'unlimited' && hasActiveSubscription ? (
                    <div className="h-9 text-center rounded border border-transparent text-gray-600 dark:text-muted-foreground flex items-center justify-center">
                      <Badge variant="secondary">Your current plan</Badge>
                    </div>
                  ) : (
                    <SubscriptionButton
                      plan={unlimitedPlan}
                      billingPeriod="year"
                      setSelectedPlanHandle={() => {}}
                      upgradePath="/upgrade/subscribe"
                      upgradeFlow={upgradeFlow}
                    />
                  )}
                </div>

                <PlanCardFeatures
                  plan="unlimited"
                  intro={
                    <>
                      Everything in{' '}
                      <span className="font-semibold text-black dark:text-foreground">Scale</span>,
                      plus:
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500 dark:text-muted-foreground">
        Nonprofit, charity, or educational institution?{' '}
        <Link href="/docs/articles/nonprofit-discount" className="underline" target="_blank">
          Get 50% off all plans
        </Link>
      </div>
    </div>
  );
};

const SubscribePage = () => {
  const { sellerId, token, seller } = useAuth();
  const {
    plan: planHandleQuery,
    billing_period: billingPeriodQuery,
    coupon: couponQuery,
    upgradeFlow: upgradeFlowQuery,
    scaleBracket: scaleBracketQuery,
  } = useUrlQuery();

  const [planHandle] = useState(planHandleQuery || 'launch');
  const [billingPeriod, setBillingPeriod] = useState(billingPeriodQuery || 'month');
  const [couponCode] = useState(couponQuery);
  const [upgradeFlow, setUpgradeFlow] = useState(upgradeFlowQuery || 'upgrade-page');

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponCode, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponCode || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponCode || !!rewardfulCoupon),
    retry: false,
  });

  const upgradePlan = getPlan(planHandle);
  const isYearlyOnly = upgradePlan.monthlyPrice === null;
  const effectiveBillingPeriod = isYearlyOnly ? 'year' : billingPeriod;

  function switchBillingPeriodToYearly() {
    setBillingPeriod('year');
    setUpgradeFlow('upgrade-page-yearly-nudge');
  }

  if (upgradePlan.handle !== 'free' && hasLiveSubscription(seller)) {
    return (
      <Redirect
        to={`/settings/plan?${stringify({
          plan: planHandle,
          billing_period: effectiveBillingPeriod,
          scaleBracket: Number(scaleBracketQuery) || undefined,
        })}`}
      />
    );
  }

  const backHref = `/upgrade?${stringify({
    plan: planHandle,
    billing_period: effectiveBillingPeriod,
    coupon: couponQuery,
    upgradeFlow,
    scaleBracket: Number(scaleBracketQuery),
  })}`;

  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      <div className="relative">
        <div className="absolute left-4 top-4 z-10">
          <Button appearance="light" to={backHref} aria-label="Back">
            <ChevronLeftIcon className="w-5 h-5" />
          </Button>
        </div>

        <div className="max-w-xl mx-auto px-4 py-4">
          <div className="text-center mb-8">
            <h1 className="text-xl font-bold mb-6">
              {upgradePlan.handle === 'free' ? 'Start for free' : `Upgrade to ${upgradePlan.title}`}
            </h1>

            <PriceBreakdown
              plan={upgradePlan}
              coupon={coupon}
              billingPeriod={effectiveBillingPeriod}
              scaleBracket={Number(scaleBracketQuery)}
            />

            {upgradePlan.handle !== 'free' && !isYearlyOnly && billingPeriod === 'month' && (
              <SwitchToYearlyOffer
                plan={upgradePlan}
                scaleBracket={Number(scaleBracketQuery)}
                onSwitch={switchBillingPeriodToYearly}
              />
            )}
          </div>

          <div className="max-w-xl m-auto">
            <UpgradeForm
              onSuccessPath="/"
              planHandle={planHandle}
              billingPeriod={effectiveBillingPeriod}
              couponQuery={couponQuery}
              upgradeFlow={upgradeFlow}
              scaleBracket={Number(scaleBracketQuery)}
            />
          </div>
        </div>
      </div>
    </Elements>
  );
};

const UpgradePage = () => {
  return (
    <Switch>
      <Route path="/upgrade" exact component={PlansPage} />

      <Route path="/upgrade/subscribe" exact component={SubscribePage} />
    </Switch>
  );
};

export default UpgradePage;
